import { type Assign, type HTMLProps, Menu } from "@ark-ui/solid";
import { ChevronRight } from "lucide-solid";
import {
	type Accessor,
	type Component,
	type ComponentProps,
	For,
	type JSXElement,
	Match,
	Show,
	Switch
} from "solid-js";
import { Dynamic, Portal } from "solid-js/web";
import { twMerge } from "tailwind-merge";
import { tv, type VariantProps } from "tailwind-variants";
import { createStyleContext } from "~/solidJs/shared/helpers/create-style-context";
import type { ElementType } from "~/solidJs/shared/helpers/types";

const menu = tv(
	{
		base: "menu",
		defaultVariants: { size: "md" },
		slots: {
			contextTrigger: "menu__contextTrigger",
			trigger: "",
			triggerItem:
				"caption-11-medium flex shrink-0 cursor-pointer items-start rounded-md px-2 py-1.5 text-text-secondary-v2 hover:bg-interaction-white-hv data-[highlighted]:bg-interaction-white-hv data-[open]:bg-interaction-white-hv data-[state=open]:bg-interaction-white-hv",
			indicator: "menu__indicator",
			positioner: "z-[1000]",
			arrow: "menu__arrow",
			arrowTip: "menu__arrowTip",
			content:
				"z-[1000] flex max-h-96 min-w-56 flex-col overflow-auto rounded-2xl border border-border-secondary bg-white p-3 shadow-lg outline-none data-[state=open]:animate-fadeIn",
			separator: "my-1.5 h-px w-full shrink-0 border-none bg-border-secondary",
			item: "caption-11-medium flex shrink-0 cursor-pointer items-center justify-start rounded-md px-2 py-1.5 text-text-secondary-v2 hover:bg-interaction-white-hv data-[disabled]:pointer-events-none data-[highlighted]:bg-interaction-white-hv data-[open]:bg-interaction-white-hv data-[disabled]:text-text-tertiary",
			optionItem: "menu__optionItem",
			optionItemIndicator: "menu__optionItemIndicator",
			optionItemText: "menu__optionItemText",
			itemGroupLabel: "pb-2 text-2xs text-text-secondary",
			itemGroup: "flex flex-col"
		},
		variants: {
			size: {
				xs: {
					contextTrigger: "menu__contextTrigger--size_xs",
					trigger: "menu__trigger--size_xs",
					triggerItem: "menu__triggerItem--size_xs",
					indicator: "menu__indicator--size_xs",
					positioner: "menu__positioner--size_xs",
					arrow: "menu__arrow--size_xs",
					arrowTip: "menu__arrowTip--size_xs",
					separator: "menu__separator--size_xs",
					item: "",
					optionItem: "menu__optionItem--size_xs",
					optionItemIndicator: "menu__optionItemIndicator--size_xs",
					optionItemText: "menu__optionItemText--size_xs",
					itemGroupLabel: "menu__itemGroupLabel--size_xs",
					itemGroup: "menu__itemGroup--size_xs"
				},
				sm: {
					contextTrigger: "menu__contextTrigger--size_sm",
					trigger: "menu__trigger--size_sm",
					triggerItem: "menu__triggerItem--size_sm",
					indicator: "menu__indicator--size_sm",
					positioner: "menu__positioner--size_sm",
					arrow: "menu__arrow--size_sm",
					arrowTip: "menu__arrowTip--size_sm",
					separator: "menu__separator--size_sm",
					item: "",
					optionItem: "menu__optionItem--size_sm",
					optionItemIndicator: "menu__optionItemIndicator--size_sm",
					optionItemText: "menu__optionItemText--size_sm",
					itemGroupLabel: "menu__itemGroupLabel--size_sm",
					itemGroup: "menu__itemGroup--size_sm"
				},
				md: {
					contextTrigger: "menu__contextTrigger--size_md",
					trigger: "menu__trigger--size_md",
					triggerItem: "menu__triggerItem--size_md",
					indicator: "menu__indicator--size_md",
					positioner: "menu__positioner--size_md",
					arrow: "menu__arrow--size_md",
					arrowTip: "menu__arrowTip--size_md",
					separator: "menu__separator--size_md",
					item: "",
					optionItem: "menu__optionItem--size_md",
					optionItemIndicator: "menu__optionItemIndicator--size_md",
					optionItemText: "menu__optionItemText--size_md",
					itemGroupLabel: "menu__itemGroupLabel--size_md",
					itemGroup: "menu__itemGroup--size_md"
				},
				lg: {
					contextTrigger: "menu__contextTrigger--size_lg",
					trigger: "menu__trigger--size_lg",
					triggerItem: "menu__triggerItem--size_lg",
					indicator: "menu__indicator--size_lg",
					positioner: "menu__positioner--size_lg",
					arrow: "menu__arrow--size_lg",
					arrowTip: "menu__arrowTip--size_lg",
					separator: "menu__separator--size_lg",
					item: "",
					optionItem: "menu__optionItem--size_lg",
					optionItemIndicator: "menu__optionItemIndicator--size_lg",
					optionItemText: "menu__optionItemText--size_lg",
					itemGroupLabel: "menu__itemGroupLabel--size_lg",
					itemGroup: "menu__itemGroup--size_lg"
				}
			}
		}
	},
	{ twMerge: true }
);
const { withRootProvider, withContext } = createStyleContext(menu);
export type MenuVariationProps = VariantProps<typeof menu>;
export type RootProviderProps = ComponentProps<typeof RootProvider>;
export const RootProvider = withRootProvider<Assign<Menu.RootProviderProps, MenuVariationProps>>(
	Menu.RootProvider
);

export type RootProps = ComponentProps<typeof Root>;
export const Root = withRootProvider<Assign<Menu.RootProps, MenuVariationProps>>(Menu.Root);

export const Arrow = withContext<Assign<HTMLProps<"div">, Menu.ArrowBaseProps>>(
	Menu.Arrow,
	"arrow"
);

export const ArrowTip = withContext<Assign<HTMLProps<"div">, Menu.ArrowTipBaseProps>>(
	Menu.ArrowTip,
	"arrowTip"
);

export const CheckboxItem = withContext<Assign<HTMLProps<"div">, Menu.CheckboxItemBaseProps>>(
	Menu.CheckboxItem,
	"item"
);

export const Content = withContext<Assign<HTMLProps<"div">, Menu.ContentBaseProps>>(
	Menu.Content,
	"content"
);

export const ContextTrigger = withContext<
	Assign<HTMLProps<"button">, Menu.ContextTriggerBaseProps>
>(Menu.ContextTrigger, "contextTrigger");

export const Indicator = withContext<Assign<HTMLProps<"div">, Menu.IndicatorBaseProps>>(
	Menu.Indicator,
	"indicator"
);

export const ItemGroupLabel = withContext<Assign<HTMLProps<"div">, Menu.ItemGroupLabelBaseProps>>(
	Menu.ItemGroupLabel,
	"itemGroupLabel"
);

export const ItemGroup = withContext<Assign<HTMLProps<"div">, Menu.ItemGroupBaseProps>>(
	Menu.ItemGroup,
	"itemGroup"
);

export const ItemIndicator = withContext<Assign<HTMLProps<"div">, Menu.ItemIndicatorBaseProps>>(
	Menu.ItemIndicator,
	"indicator"
);

export const Item = withContext<Assign<HTMLProps<"div">, Menu.ItemBaseProps>>(Menu.Item, "item");

export const ItemText = withContext<Assign<HTMLProps<"div">, Menu.ItemTextBaseProps>>(
	Menu.ItemText,
	"optionItemText"
);

export const Positioner = withContext<Assign<HTMLProps<"div">, Menu.PositionerBaseProps>>(
	Menu.Positioner,
	"positioner"
);

export const RadioItemGroup = withContext<Assign<HTMLProps<"div">, Menu.RadioItemGroupBaseProps>>(
	Menu.RadioItemGroup,
	"itemGroup"
);

export const RadioItem = withContext<Assign<HTMLProps<"div">, Menu.RadioItemBaseProps>>(
	Menu.RadioItem,
	"item"
);

export const Separator = withContext<Assign<HTMLProps<"hr">, Menu.SeparatorBaseProps>>(
	Menu.Separator,
	"separator"
);

export const TriggerItem = withContext<Assign<HTMLProps<"div">, Menu.TriggerItemBaseProps>>(
	Menu.TriggerItem,
	"triggerItem"
);

export const Trigger = withContext<Assign<HTMLProps<"button">, Menu.TriggerBaseProps>>(
	Menu.Trigger,
	"trigger"
);

export { MenuContext as Context } from "@ark-ui/solid";

export enum ClosedMenuItemType {
	Item = "item",
	Group = "group",
	Separator = "separator",
	CustomElement = "customElement"
}

export type ClosedMenuItem = {
	id: string;
	type: ClosedMenuItemType.Item;
	label?: string;
	renderItem?: <E extends ElementType>(props: ComponentProps<E>) => JSXElement;
	Icon?: Component<ComponentProps<"svg">>;
	onSelect?: (props: ClosedMenuItem) => void;
	getIsDisabled?: Accessor<boolean>;
};

export type ClosedMenuSeparatorItem = {
	type: ClosedMenuItemType.Separator;
};

export type ClosedMenuCustomElement = {
	type: ClosedMenuItemType.CustomElement;
	element: Component;
};

export type ClosedMenuGroupItem = {
	id: string;
	type: ClosedMenuItemType.Group;
	label?: string;
	renderItem?: Component<ClosedMenuGroupItem>;
	Icon?: Component<ComponentProps<"svg">>;
	items: Array<ClosedMenuTypesUnion>;
	positioning?: Menu.RootProps["positioning"];
};

export type ClosedMenuTypesUnion =
	| ClosedMenuSeparatorItem
	| ClosedMenuGroupItem
	| ClosedMenuItem
	| ClosedMenuCustomElement;

export interface MenuClosedComponentProps {
	items: Array<ClosedMenuTypesUnion>;
	trigger: Component<ComponentProps<"button">>;
	rootProps?: Menu.RootProps & { portal?: boolean };
	/**
	 * do not passed recursivly to sub menues
	 */
	onceRootProps?: Menu.RootProps & { portal?: boolean };
	contentClass?: string;
	portalMount?: () => Element;
}

type RenderClosedItemOrGroupProps = {
	parentRootProps?: Menu.RootProps & { portal?: boolean };
} & ClosedMenuTypesUnion;

export const RenderClosedItemOrGroup: Component<RenderClosedItemOrGroupProps> = (props) => {
	return (
		<Switch>
			<Match when={props.type === ClosedMenuItemType.Item && props} keyed>
				{(passedItem) => (
					<Switch
						fallback={
							<Item
								data-test={passedItem.id}
								onSelect={() => passedItem.onSelect?.(passedItem)}
								value={passedItem.id}
								disabled={passedItem.getIsDisabled?.()}
							>
								<div class="flex w-full items-center justify-start gap-2">
									<Show when={passedItem.Icon} keyed>
										{(Icon) => <Icon class="" />}
									</Show>
									{passedItem.label}
								</div>
							</Item>
						}
					>
						<Match when={passedItem.renderItem} keyed>
							{(RenderFc) => (
								<Item
									data-test={passedItem.id}
									onSelect={() => passedItem.onSelect?.(passedItem)}
									value={passedItem.id}
									asChild={(p) => <Dynamic component={RenderFc} {...p()} />}
								>
									<div class="flex w-full items-center justify-start gap-2">
										<Show when={passedItem.Icon} keyed>
											{(Icon) => <Icon />}
										</Show>
										{passedItem.label}
									</div>
								</Item>
							)}
						</Match>
					</Switch>
				)}
			</Match>
			<Match when={props.type === ClosedMenuItemType.CustomElement && props} keyed>
				{(p) => <Dynamic component={p.element} />}
			</Match>
			<Match when={props.type === ClosedMenuItemType.Separator}>
				<Separator />
			</Match>
			<Match when={props.type === ClosedMenuItemType.Group && props} keyed>
				{(passedItem) => (
					<Root
						unmountOnExit
						{...(props.parentRootProps ? props.parentRootProps : {})}
						positioning={{
							offset: { mainAxis: 20 },
							...(passedItem.positioning ? passedItem.positioning : {})
						}}
					>
						<TriggerItem data-test={passedItem.id}>
							<Switch
								fallback={
									<div class="flex w-full items-center justify-between">
										<div class="flex gap-2">
											<Dynamic component={passedItem.Icon} />
											{passedItem.label}
										</div>
										<ChevronRight size={14} />
									</div>
								}
							>
								<Match when={passedItem.renderItem} keyed>
									{(RenderFc) => <RenderFc {...passedItem} />}
								</Match>
							</Switch>
						</TriggerItem>
						<Switch
							fallback={
								<Positioner>
									<Content class="z-10 min-w-56">
										<For each={passedItem.items}>
											{(groupItem) => (
												<RenderClosedItemOrGroup
													{...groupItem}
													parentRootProps={props.parentRootProps}
												/>
											)}
										</For>
									</Content>
								</Positioner>
							}
						>
							<Match when={props.parentRootProps?.portal}>
								<Portal>
									<Positioner>
										<Content class="z-10 min-w-56">
											<For each={passedItem.items}>
												{(groupItem) => (
													<RenderClosedItemOrGroup
														{...groupItem}
														parentRootProps={props.parentRootProps}
													/>
												)}
											</For>
										</Content>
									</Positioner>
								</Portal>
							</Match>
						</Switch>
					</Root>
				)}
			</Match>
		</Switch>
	);
};

export const MenuClosedComponent: Component<MenuClosedComponentProps> = (props) => {
	return (
		<Root
			{...(props.rootProps ? props.rootProps : {})}
			{...(props.onceRootProps ? props.onceRootProps : {})}
		>
			<Trigger asChild={(p) => <props.trigger {...p()} />} />
			<Switch>
				<Match when={props.rootProps?.portal === false}>
					<Positioner>
						<Content
							on:wheel={(e) => {
								e.stopPropagation();
							}}
							class={twMerge(
								"pointer-events-auto z-10 min-w-56 overflow-hidden",
								props.contentClass
							)}
						>
							<div class="overflow-auto">
								<For each={props.items}>
									{(item) => (
										<RenderClosedItemOrGroup
											{...item}
											parentRootProps={props.rootProps}
										/>
									)}
								</For>
							</div>
						</Content>
					</Positioner>
				</Match>
				<Match when={true}>
					<Portal mount={props.portalMount?.()}>
						<Positioner>
							<Content
								on:wheel={(e) => {
									e.stopPropagation();
								}}
								class={twMerge(
									"pointer-events-auto z-10 min-w-56 overflow-hidden",
									props.contentClass
								)}
							>
								<div class="overflow-auto">
									<For each={props.items}>
										{(item) => (
											<RenderClosedItemOrGroup
												{...item}
												parentRootProps={props.rootProps}
											/>
										)}
									</For>
								</div>
							</Content>
						</Positioner>
					</Portal>
				</Match>
			</Switch>
		</Root>
	);
};
